import { asApiDate } from './daily-diary-filters.util';

/**
 * Single source of truth for which of the Daily diary tab's TWO modes is
 * active. Four components ask this question and they all have to agree:
 *
 *   `app-daily-diary-wrapper`  what to render in the tab body
 *   `app-site-details-screen`  whether the filter chips render on the tab row
 *   `app-projects-wrapper`     whether the floating + renders over the map
 *   `app-daily-diaries-list`   where a card click navigates to
 *
 * Every one of them resolves the mode straight off the query params, so a cold
 * load of either URL shape lands in the right mode without any in-app
 * navigation having happened.
 *
 * Mode A — LIST:   no `diaryDate`. Chips + card grid + floating +, no date strip.
 * Mode B — DETAIL: `diaryDate=YYYY-MM-DD`. Date strip + that day's diaries
 *                  rendered as component sections; no chips and no +.
 *
 * Why a diary-specific `diaryDate` and not the `date` param the Daily report
 * tab uses: `mat-tab-group` instantiates every tab body eagerly, so
 * `DailyReportWrapperComponent` is alive even while the diary tab is showing,
 * and its ngOnInit seeds `date=<today>` whenever the param is missing. `date`
 * is therefore ALWAYS present and cannot discriminate between the two modes.
 * `diaryDate` is only ever written by the diary tab.
 */
export const DIARY_DATE_PARAM = 'diaryDate';

/** The selected diary within a day (Mode B), analogous to the report's `reportId`. */
export const DIARY_ID_PARAM = 'diaryId';

/**
 * Which component section is expanded in Mode B.
 *
 * Deliberately NOT the daily report's `expandedSection`: `DailyReportComponent`
 * reacts to that param with `$('#' + expandedSection)[0].scrollIntoView(...)`,
 * and it can be mounted at the same time as the diary tab (the reports list
 * auto-selects the first report for the current date). A diary-only section
 * name would make that lookup return undefined and throw.
 */
export const DIARY_SECTION_PARAM = 'diarySection';

/**
 * Which site the open diary belongs to. The list is project-wide, so the
 * record view needs to be told which site to load the day from — on the site
 * page this was implicit in the route.
 */
export const DIARY_SITE_PARAM = 'diarySite';

export type DiaryViewMode = 'list' | 'detail';

/** '' when the tab is in list mode. */
export function resolveDiaryDetailDate(queryParams: any): string {
  return asApiDate(queryParams?.[DIARY_DATE_PARAM]);
}

export function resolveDiaryViewMode(queryParams: any): DiaryViewMode {
  return resolveDiaryDetailDate(queryParams) ? 'detail' : 'list';
}

export function isDiaryDetailMode(queryParams: any): boolean {
  return resolveDiaryViewMode(queryParams) === 'detail';
}
